import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import { createGameState } from '@/engine/state/createGameState';
import {
  dispatch, rollAction, chooseMoveAction, stopAction, ackBustAction,
  finalStandings, canRoll, canStop,
} from '@/engine/actions';
import { decideAction, personalityFor, PERSONALITIES } from '@/engine/ai/heuristicBot';
import { PHASES, ACTION_TYPES } from '@/engine/constants';
import { auth, googleProvider, firebaseConfigured } from '@/firebase';
import { rollAnimationMs } from '@/utils/rollTheatre';
import { ensureSiteUserRecord, watchSiteUserStatus } from '@/store/siteAccess';
import {
  createRoom, joinRoom, leaveLobby, startRoom, bindRoom, commitAction, setTableSize,
  sweepMyRooms, recordWaiting,
  shouldAutoPlay, personalityForSeat, seqOf, DISCONNECT_GRACE_MS,
} from '@/store/multiplayer';
import { outcomeOf, fingerprintOf, recordFinishedGame } from '@/store/userStats';

// The single place engine `dispatch()` is called from, same convention as the
// sibling repos. Components never touch engine state directly; they commit
// intents and read getters.

const SAVE_KEY = 'cant-stop:saved-game';
const SETTINGS_KEY = 'cant-stop:settings';

// How long a bot "thinks" before each kind of action. Not a fake difficulty
// knob - it exists so a chain of bot actions is legible as a sequence of
// decisions rather than the board flickering through a whole turn at once.
// Can't Stop bot turns are LONG (roll, choose, roll, choose, ..., stop), so
// the individual pauses are shorter than no-thanks's - it's the sum that has
// to stay watchable.
//
// The bust pause is the outlier on purpose: a bust is the game's whole
// dramatic payload, and it needs a beat where the table just looks at the
// dice that did it. The jitter fraction is the house 0.3 (see no-thanks).
// Paces re-tuned for the 3D roll (2026-08-29): a roll now plays out as
// shake + throw + settle, ~2.1s (GameTable's startTumble), and the pause
// BEFORE a bot's next action has to outlast it - an action landing
// mid-animation hits the seq watcher's else-branch and snaps the dice to
// rest, which is exactly the un-drama the animation exists to fix. So
// CHOOSE_MOVE and ACK_BUST both clear 2.1s with reading time on top, and
// ROLL shrinks to a beat - the shake itself is the anticipation now.
// These are READ BEATS, not animation budgets. A decision that follows a
// roll (choosing a pairing, acknowledging a bust) waits for the roll's OWN
// animation first - pauseForDecision computes the exact landing from
// rollTheatre's deterministic durations and adds the beat on top. The old
// arrangement baked the worst-case animation into these bases, so any roll
// whose dice landed early sat in dead air (Matt, 2026-08-29: "pretty long
// delay after the dice have landed before the computers make their
// choices").
export const BOT_PAUSE_MS = {
  [ACTION_TYPES.ROLL]: 400,
  [ACTION_TYPES.CHOOSE_MOVE]: 700,
  [ACTION_TYPES.STOP]: 900,
  [ACTION_TYPES.ACK_BUST]: 1300,
};
export const BOT_PAUSE_JITTER = 0.3;

/**
 * A pause for one bot action: the base for that action type, spread across a
 * window. `Math.random` is fine here and forbidden three directories away -
 * this is presentation, not the engine.
 */
export const botPause = (base, random = Math.random) => (
  Math.round(base * (1 + (random() * 2 - 1) * BOT_PAUSE_JITTER))
);

/**
 * The full pause before a bot decision: the jittered read beat, plus - for
 * the two decisions that follow a roll - exactly that roll's animation.
 * CHOOSE_MOVE and ACK_BUST can only ever occur right after a roll (the
 * engine's turn phases guarantee it), which is why the addition keys on the
 * decision type. rollAnimationMs reads the same deterministic durations the
 * cubes were dealt, and returns 0 for reduced-motion viewers - no waiting
 * on an animation nobody is watching.
 */
export const pauseForDecision = (game, type, random = Math.random) => {
  const beat = botPause(BOT_PAUSE_MS[type] ?? 700, random);
  if (type === ACTION_TYPES.CHOOSE_MOVE || type === ACTION_TYPES.ACK_BUST) {
    return rollAnimationMs(game) + beat;
  }
  return beat;
};

// The pending bot-turn timer, if any. Module-level rather than in state
// because it's a handle, not data - it must never be serialized into a save.
// Its job is to keep exactly ONE bot chain alive at a time; see the
// no-thanks repo's store for the double-chain lockup this prevents.
let botTimer = null;

// Live listener teardown for the current room, and the auth watcher.
let unbindRoomFn = null;
let unwatchStatusFn = null;
// When each absent player was last seen. Purely local observation.
const lastSeen = {};

const cancelBotTurn = () => {
  if (botTimer !== null) {
    window.clearTimeout(botTimer);
    botTimer = null;
  }
};

const loadJson = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveJson = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A full or disabled localStorage must never break play.
  }
};

export default createStore({
  state: {
    game: null,
    thinkingPlayerId: null,
    settings: loadJson(SETTINGS_KEY) || { playerName: 'You', opponents: 2 },
    hasSavedGame: Boolean(loadJson(SAVE_KEY)),

    // ---- sign-in and the shared approval gate
    firebaseConfigured,
    authReady: !firebaseConfigured,
    // Separate from authReady: Firebase Auth can answer before the
    // siteUsers watcher has, and the gate must not flash "waiting for
    // approval" at an approved player in that gap.
    siteUserReady: !firebaseConfigured,
    user: null,
    siteUser: null,          // { status: 'pending'|'approved'|'denied', isAdmin }

    // ---- online play
    roomId: null,
    roomMeta: null,
    presence: {},
    roomError: null,
  },

  getters: {
    inPlay: (state) => Boolean(state.game) && state.game.meta.phase === PHASES.PLAYING,
    isFinished: (state) => Boolean(state.game) && state.game.meta.phase === PHASES.FINISHED,
    activePlayer: (state) => (state.game
      ? state.game.players[state.game.meta.activePlayerIndex]
      : null),
    humanPlayer: (state) => state.game?.players.find((p) => !p.isBot) || null,
    isHumanTurn: (state, getters) => Boolean(
      getters.inPlay && getters.activePlayer && !getters.activePlayer.isBot && !state.thinkingPlayerId,
    ),
    canRoll: (state) => (state.game ? canRoll(state.game) : false),
    canStop: (state) => (state.game ? canStop(state.game) : false),
    isOnline: (state) => Boolean(state.roomId),
    isHost: (state) => Boolean(state.roomMeta && state.user && state.roomMeta.hostUid === state.user.uid),
    myUid: (state) => state.user?.uid || null,

    // Me, in both modes: online, player ids ARE Firebase uids; locally the
    // one non-bot seat. Same getter, same wording, in every sibling game.
    me: (state) => {
      if (!state.game) return null;
      if (state.roomId) return state.game.players.find((p) => p.id === state.user?.uid) || null;
      return state.game.players.find((p) => !p.isBot) || null;
    },
    isApproved: (state) => state.siteUser?.status === 'approved',
    isAdmin: (state) => Boolean(state.siteUser?.isAdmin),
    isMyOnlineTurn: (state) => {
      if (!state.roomId || !state.game) return false;
      const active = state.game.players[state.game.meta.activePlayerIndex];
      return Boolean(active && !active.isBot && active.id === state.user?.uid);
    },
    absentPlayerIds: (state) => {
      if (!state.roomId || !state.game) return [];
      return state.game.players
        .filter((p) => !p.isBot && !state.presence[p.id])
        .map((p) => p.id);
    },
    standings: (state) => (state.game ? finalStandings(state.game) : null),
    // Opponents in seat order starting after the human, so the table reads
    // round-the-left the way play actually passes.
    opponentsInTurnOrder: (state, getters) => {
      if (!state.game) return [];
      const human = getters.humanPlayer;
      const { players } = state.game;
      const start = human ? human.seat : 0;
      return players
        .map((_, i) => players[(start + 1 + i) % players.length])
        .filter((p) => p.id !== human?.id);
    },
  },

  mutations: {
    setGame(state, game) {
      state.game = game;
      state.hasSavedGame = Boolean(game);
      saveJson(SAVE_KEY, game);
    },
    setThinking(state, playerId) {
      state.thinkingPlayerId = playerId;
    },
    setSettings(state, settings) {
      state.settings = { ...state.settings, ...settings };
      saveJson(SETTINGS_KEY, state.settings);
    },
    clearGame(state) {
      state.game = null;
      state.thinkingPlayerId = null;
      state.hasSavedGame = false;
      saveJson(SAVE_KEY, null);
    },
    setAuthReady: (state, v) => { state.authReady = v; },
    setUser: (state, user) => { state.user = user; },
    setSiteUser: (state, su) => { state.siteUser = su; state.siteUserReady = true; },
    setSiteUserReady: (state, v) => { state.siteUserReady = v; },
    setRoom: (state, { id, meta }) => { state.roomId = id; state.roomMeta = meta ?? null; },
    setRoomMeta: (state, meta) => { state.roomMeta = meta; },
    setPresence: (state, p) => { state.presence = p || {}; },
    setRoomError: (state, e) => { state.roomError = e; },
    // Online state arrives from the listener, so it must NOT be written back
    // to localStorage - that save slot is for the local game only.
    setRemoteGame(state, game) { state.game = game; },
  },

  actions: {
    // Lifetime stats, once per finished game. Safe to dispatch as often as
    // anything notices the game has ended - the fingerprint transaction in
    // userStats.js is what makes it exactly-once, not the caller.
    recordMyStats({ state, getters }) {
      const outcome = outcomeOf({
        game: state.game,
        myId: getters.me?.id,
        online: Boolean(state.roomId),
      });
      if (!outcome) return;
      recordFinishedGame({
        uid: getters.myUid,
        name: state.user?.displayName || state.user?.email || null,
        fingerprint: fingerprintOf({ game: state.game, roomId: state.roomId }),
        outcome,
      });
    },

    newGame({ commit, dispatch: dispatchAction, state }, { playerName, opponents, seed } = {}) {
      const name = playerName || state.settings.playerName || 'You';
      const botCount = opponents || state.settings.opponents || 2;

      // The human always sits first, so "pass the dice to the player on your
      // left" is literally the next row down the screen.
      const players = [
        { id: 'you', name, isBot: false },
        ...Array.from({ length: botCount }, (_, i) => ({
          id: `bot${i}`,
          name: PERSONALITIES[personalityFor(i)].label,
          isBot: true,
        })),
      ];

      cancelBotTurn();
      commit('setSettings', { playerName: name, opponents: botCount });
      commit('setGame', createGameState({ players, seed: seed ?? Date.now() }));
      dispatchAction('runBotsIfNeeded');
    },

    resumeGame({ commit, dispatch: dispatchAction }) {
      const saved = loadJson(SAVE_KEY);
      if (!saved) return;
      cancelBotTurn();
      commit('setGame', saved);
      dispatchAction('runBotsIfNeeded');
    },

    /**
     * A human's move, local or online - the four game actions funnel through
     * here so the routing decision (local dispatch vs online transaction)
     * lives in exactly one place.
     */
    play({ commit, dispatch: dispatchAction, state, getters }, action) {
      if (state.roomId) { dispatchAction('playOnline', action); return; }
      if (!getters.isHumanTurn) return;
      commit('setGame', dispatch(state.game, action));
      dispatchAction('runBotsIfNeeded');
    },

    roll({ dispatch: dispatchAction }) { dispatchAction('play', rollAction()); },
    stop({ dispatch: dispatchAction }) { dispatchAction('play', stopAction()); },
    ackBust({ dispatch: dispatchAction }) { dispatchAction('play', ackBustAction()); },
    chooseMove({ dispatch: dispatchAction }, optionIndex) {
      dispatchAction('play', chooseMoveAction(optionIndex));
    },

    /**
     * Plays out every consecutive bot action, one at a time, with a pause
     * between so the table is watchable.
     *
     * Recursive-with-a-timeout rather than a loop: a Can't Stop turn is many
     * actions (roll, choose, roll, choose... stop or bust), and it ends only
     * when the seat comes back to a human or the game finishes.
     */
    runBotsIfNeeded({ commit, dispatch: dispatchAction, state, getters }) {
      if (botTimer !== null) return;

      if (!getters.inPlay) {
        commit('setThinking', null);
        return;
      }
      const active = getters.activePlayer;
      if (!active?.isBot) {
        commit('setThinking', null);
        return;
      }

      commit('setThinking', active.id);
      const actingId = active.id;

      // Peeked at only to choose how long to pause. The decision actually
      // PLAYED is recomputed when the timer fires, from the state then.
      const preview = decideAction(state.game, personalityFor(active.seat - 1));
      const pause = pauseForDecision(state.game, preview.type);

      botTimer = window.setTimeout(() => {
        botTimer = null;
        if (!state.game || state.game.meta.phase !== PHASES.PLAYING) {
          commit('setThinking', null);
          return;
        }

        // Whoever is active NOW is who gets to move - see the no-thanks
        // repo's store for the stale-decision bug this ordering prevents.
        const current = state.game.players[state.game.meta.activePlayerIndex];
        if (!current?.isBot || current.id !== actingId) {
          commit('setThinking', null);
          dispatchAction('runBotsIfNeeded');
          return;
        }

        const decision = decideAction(state.game, personalityFor(current.seat - 1));
        commit('setGame', dispatch(state.game, decision));
        commit('setThinking', null);
        dispatchAction('runBotsIfNeeded');
      }, pause);
    },

    // ---------------------------------------------------------------- auth

    initAuth({ commit, dispatch: dispatchAction }) {
      if (!firebaseConfigured || !auth) {
        commit('setAuthReady', true);
        return;
      }
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        if (unwatchStatusFn) { unwatchStatusFn(); unwatchStatusFn = null; }
        // An anonymous session exists only so a signed-out bug report can
        // carry a token (utils/bugReports.js). It is NOT a player: treat it
        // as signed out, and never write it a siteUsers record.
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setSiteUser', null);
          commit('setAuthReady', true);
          return;
        }
        commit('setUser', user ? {
          uid: user.uid, displayName: user.displayName, email: user.email,
        } : null);
        if (user) {
          commit('setSiteUserReady', false);
          await ensureSiteUserRecord(user);
          unwatchStatusFn = watchSiteUserStatus(user.uid, (su) => commit('setSiteUser', su));
          if (user.displayName) dispatchAction('rememberName', user.displayName);
        } else {
          commit('setSiteUser', null);
        }
        commit('setAuthReady', true);
      });
    },

    rememberName({ commit, state }, name) {
      if (!name || state.settings.playerName === name) return;
      commit('setSettings', { playerName: name });
    },

    async signIn({ commit }) {
      if (!auth) throw new Error('Sign-in needs Firebase configured.');
      try {
        markInteractiveSignIn();
        await signInWithPopup(auth, googleProvider);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async signOutOfSite({ commit, dispatch: dispatchAction }) {
      await dispatchAction('leaveRoom');
      if (auth) { await endSharedSignIn(); await signOut(auth); }
      commit('setUser', null);
      commit('setSiteUser', null);
    },

    // ------------------------------------------------------------ rooms

    sweepRooms({ state }) {
      if (!state.user) return;
      sweepMyRooms({ user: state.user }).catch(() => {});
    },

    async hostRoom({ commit, state, dispatch: dispatchAction }, { tableSize, async: asyncRoom = false } = {}) {
      trackEvent('room-hosted');
      commit('setRoomError', null);
      try {
        const { gameId } = await createRoom({
          user: state.user,
          tableSize: tableSize ?? (state.settings.opponents || 2) + 1,
          async: asyncRoom,
        });
        await dispatchAction('enterRoom', gameId);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async joinRoomByCode({ commit, state, dispatch: dispatchAction }, code) {
      trackEvent('room-joined');
      commit('setRoomError', null);
      try {
        const { gameId } = await joinRoom({ user: state.user, code });
        await dispatchAction('enterRoom', gameId);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    enterRoom({ commit, state, dispatch: dispatchAction }, gameId) {
      cancelBotTurn();
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      commit('setRoom', { id: gameId, meta: null });
      commit('setRemoteGame', null);

      unbindRoomFn = bindRoom({
        gameId,
        user: state.user,
        onMeta: (meta) => {
          commit('setRoomMeta', meta);
          if (meta === null) dispatchAction('leaveRoom');
        },
        onState: (game) => {
          commit('setRemoteGame', game);
          dispatchAction('driveOnlineTurn');
        },
        onPresence: (presence) => {
          const now = Date.now();
          Object.keys(lastSeen).forEach((uid) => { if (presence[uid]) delete lastSeen[uid]; });
          (state.game?.players || []).forEach((p) => {
            if (!p.isBot && !presence[p.id] && !lastSeen[p.id]) lastSeen[p.id] = now;
          });
          commit('setPresence', presence);
          dispatchAction('driveOnlineTurn');
        },
      });
    },

    async leaveRoom({ commit, state }) {
      cancelBotTurn();
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      if (state.roomId && state.user) {
        try { await leaveLobby({ user: state.user, gameId: state.roomId }); } catch { /* best effort */ }
      }
      commit('setRoom', { id: null, meta: null });
      commit('setPresence', {});
      commit('setRemoteGame', null);
      commit('setThinking', null);
    },

    async changeTableSize({ commit, state }, tableSize) {
      try {
        await setTableSize({
          user: state.user, gameId: state.roomId, meta: state.roomMeta, tableSize,
        });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async dealRoom({ commit, state }) {
      commit('setRoomError', null);
      try {
        await startRoom({ user: state.user, gameId: state.roomId, meta: state.roomMeta });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    // ------------------------------------------------- the online turn loop

    /**
     * Called on every state and presence change. Works out whether THIS
     * browser owes the table a move, and if so plays it. Only ever fires for
     * a bot seat or somebody who has dropped out - see multiplayer.js's
     * shouldAutoPlay for the two-questions rule.
     */
    driveOnlineTurn({ state, dispatch: dispatchAction, commit }) {
      if (!state.roomId || !state.game || botTimer !== null) return;
      if (state.game.meta.phase !== PHASES.PLAYING) { commit('setThinking', null); return; }

      const mine = shouldAutoPlay({
        gameState: state.game,
        presence: state.presence,
        myUid: state.user?.uid,
        disconnectedSince: lastSeen,
        asyncRoom: Boolean(state.roomMeta?.async),
      });
      if (!mine) {
        const active = state.game.players[state.game.meta.activePlayerIndex];
        if (active && !active.isBot && !state.presence[active.id] && lastSeen[active.id]) {
          const waited = Date.now() - lastSeen[active.id];
          if (waited < DISCONNECT_GRACE_MS) {
            botTimer = window.setTimeout(() => {
              botTimer = null;
              dispatchAction('driveOnlineTurn');
            }, DISCONNECT_GRACE_MS - waited + 100);
          }
        }
        commit('setThinking', null);
        return;
      }

      const active = state.game.players[state.game.meta.activePlayerIndex];
      commit('setThinking', active.id);
      const preview = decideAction(state.game, personalityForSeat(active));
      const pause = pauseForDecision(state.game, preview.type);

      botTimer = window.setTimeout(async () => {
        botTimer = null;
        commit('setThinking', null);
        if (!state.roomId || !state.game || state.game.meta.phase !== PHASES.PLAYING) return;
        const expectedSeq = seqOf(state.game);
        try {
          const result = await commitAction({
            gameId: state.roomId,
            expectedSeq,
            // Recomputed against whatever the transaction actually finds.
            apply: (current) => {
              const seat = current.players[current.meta.activePlayerIndex];
              if (!seat) return null;
              const decision = decideAction(current, personalityForSeat(seat));
              return dispatch(current, decision);
            },
          });
          if (result.committed) recordWaiting({ gameId: state.roomId, gameState: result.state, meta: state.roomMeta });
        } catch (error) {
          commit('setRoomError', error.message);
        }
      }, pause);
    },

    /**
     * A human's own move in an online game. Same engine action as local play;
     * only the write target differs.
     */
    async playOnline({ state, commit, getters }, action) {
      if (!getters.isMyOnlineTurn) return;
      const expectedSeq = seqOf(state.game);
      try {
        const { committed, state: committedState } = await commitAction({
          gameId: state.roomId,
          expectedSeq,
          apply: (current) => {
            const seat = current.players[current.meta.activePlayerIndex];
            if (!seat || seat.id !== state.user?.uid) return null;
            return dispatch(current, action);
          },
        });
        if (committed) recordWaiting({ gameId: state.roomId, gameState: committedState, meta: state.roomMeta });
        if (!committed) commit('setRoomError', null);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    abandonGame({ commit }) {
      cancelBotTurn();
      commit('clearGame');
    },
  },
});
